import { useState } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Settings, Volume2 } from "lucide-react";
import type { Notification, NotificationEventType, User } from "@lumika/shared";
import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Label,
  PageError,
  PageLoading,
  Switch,
  cn,
  toast,
} from "@lumika/ui";
import { bdcApi } from "../api/bdc";
import {
  escalationSoundEnabled,
  setEscalationSoundEnabled,
  setSlaSoundEnabled,
  slaSoundEnabled,
} from "../lib/sound";
import { notificationsApi } from "../api/notifications";
import { MentionedText } from "./MentionedText";
import {
  formatDateTime,
  notificationLabel,
  summarizeChangeBody,
} from "@lumika/ui";
import { userDisplayName } from "../lib/user";
import {
  NOTIFICATION_PRESENTATION_COPY,
  assistantNotificationCopy,
  notificationPresentation,
} from "../lib/notification-presentation";

function notificationTitle(notification: Pick<Notification, "eventKey" | "title">): string {
  return notification.eventKey.startsWith("ai.")
    ? assistantNotificationCopy(notification.title)
    : notification.title;
}

function NotificationPresentationBadge({
  event,
}: {
  event: NotificationEventType | undefined;
}) {
  if (!event) return null;
  const presentation = notificationPresentation(event);

  return (
    <Badge variant="outline">
      {NOTIFICATION_PRESENTATION_COPY[presentation].label}
    </Badge>
  );
}

/**
 * The notification body. A field-change notification renders its
 * compact diff summary; every other body may carry `@[id]` mention
 * tokens, so it is rendered through `MentionedText` to show mention
 * chips — consistent with the timeline and task bodies. Chips are
 * non-interactive here: the whole row is already a button, so nested
 * popover buttons would be invalid HTML and unreachable anyway.
 */
function NotificationBody({ body, users }: { body: string; users: User[] }) {
  // The userName resolver turns assignee ids in field-change diffs into
  // real names — without it every assignment reads "a teammate".
  const summary = summarizeChangeBody(body, {
    userName: (id) => {
      const user = users.find((u) => u.id === id);
      return user ? userDisplayName(user) : undefined;
    },
  });
  if (summary) {
    return <p className="mt-0.5 text-muted-foreground">{summary}</p>;
  }
  return (
    <MentionedText
      body={body}
      users={users}
      interactive={false}
      className="mt-0.5 text-muted-foreground"
    />
  );
}

/**
 * Resolve the in-app destination for a notification so clicking it
 * opens the entity it is connected to — a lead, contact,
 * appointment, task, vehicle, user, or trade-in. Leads and
 * contacts have a dedicated detail page; the rest route to the page
 * that lists them. `null` only when nothing is linkable.
 */
function routeForNotification(n: Notification): string | null {
  const { sourceRecordType, sourceRecordId, payload } = n;
  const payloadStr = (key: string): string | null =>
    typeof payload[key] === "string" ? (payload[key] as string) : null;

  // Prefer the typed source record stamped on the notification.
  if (sourceRecordId) {
    switch (sourceRecordType) {
      case "lead":
        return `/inbox/${sourceRecordId}`;
      case "contact":
        return `/contacts/${sourceRecordId}`;
      case "appointment":
        // The calendar opens this exact appointment from the
        // `appointmentId` param (fetched by id, any window).
        return `/calendar?appointmentId=${sourceRecordId}`;
      case "task":
        // Deep-link to the specific task — the Tasks page opens it from
        // the `task` query param, so a @mention lands on the entity.
        return `/tasks?task=${sourceRecordId}`;
      case "follow_up":
        // A follow-up IS a task — open it in the Tasks page editor.
        return `/tasks?task=${sourceRecordId}`;
      case "vehicle":
      case "inventory_vehicle":
        return "/inventory";
      case "user":
        return "/settings/team";
      // `trade_in` has no page of its own — it lives on the owning
      // contact's Customer 360, reached via the payload contactId below.
    }
  }

  // Payload id hints — for events with no routable source record.
  const leadId = payloadStr("leadId");
  if (leadId) return `/inbox/${leadId}`;
  const contactId = payloadStr("contactId");
  if (contactId) return `/contacts/${contactId}`;
  const taskId = payloadStr("taskId") ?? payloadStr("followUpId");
  if (taskId) return `/tasks?task=${taskId}`;
  const appointmentId = payloadStr("appointmentId");
  if (appointmentId) return `/calendar?appointmentId=${appointmentId}`;
  if (payloadStr("vehicleId") ?? payloadStr("inventoryVehicleId")) {
    return "/inventory";
  }
  if (payloadStr("channelId")) return "/settings/channels";
  return null;
}

/**
 * The notifications feed, in a modal opened from the bell. The unread
 * filter is on by default; switching it off shows every notification.
 * Clicking a notification marks it read and jumps to the entity it is
 * connected to.
 */
export function NotificationsModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [unreadOnly, setUnreadOnly] = useState(true);
  // §5.5 chime toggles — per-user, persisted in localStorage like
  // lumika.theme. Escalations default ON; SLA breaches are opt-in.
  const [soundEscalations, setSoundEscalations] = useState(escalationSoundEnabled);
  const [soundSla, setSoundSla] = useState(slaSoundEnabled);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const status = unreadOnly ? "unread" : "all";

  const listQuery = useInfiniteQuery({
    queryKey: ["notifications", status],
    queryFn: ({ pageParam }) => notificationsApi.list(status, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: open,
  });
  // Roster for resolving `@[id]` mention tokens in notification bodies.
  const usersQuery = useQuery({
    queryKey: ["bdc", "users"],
    queryFn: bdcApi.users,
    enabled: open,
  });
  const users = usersQuery.data ?? [];
  const settingsQuery = useQuery({
    queryKey: ["notification-settings"],
    queryFn: () => notificationsApi.getSettings(),
    enabled: open,
  });
  const eventCatalog = new Map(
    (settingsQuery.data?.events ?? []).map((preference) => [
      preference.event.key,
      preference.event,
    ]),
  );

  const markRead = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
    onError: (e: Error) => toast.error(e.message),
  });
  const markAllRead = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["notifications"] });
      toast.success("All notifications marked read");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const items = listQuery.data?.pages.flatMap((p) => p.items) ?? [];
  // Server-side unread count (the bell keeps this cache warm). The
  // loaded pages may not include older unread rows, so a locally
  // derived count would wrongly disable "Mark all read".
  const countQuery = useQuery({
    queryKey: ["notifications", "count"],
    queryFn: () => notificationsApi.count(),
    enabled: open,
  });
  const unreadCount =
    countQuery.data?.unread ?? items.filter((n) => !n.readAt).length;

  // Clicking a notification marks it read, closes the modal, and jumps
  // to the entity it is about.
  function openNotification(n: Notification): void {
    if (!n.readAt) markRead.mutate(n.id);
    const route = routeForNotification(n);
    onClose();
    if (route) navigate(route);
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Notifications</DialogTitle>
          <DialogDescription>
            Updates and system alerts. Use Inbox Needs you as your work queue.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Switch
              id="notifications-unread-only"
              checked={unreadOnly}
              onCheckedChange={setUnreadOnly}
            />
            <Label htmlFor="notifications-unread-only" className="text-sm">
              Unread only
            </Label>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              disabled={markAllRead.isPending || unreadCount === 0}
              onClick={() => markAllRead.mutate()}
            >
              <CheckCheck className="h-4 w-4" /> Mark all read
            </Button>
            {/* Personal notification preferences — reachable from the
                bell for every role, including agents without the
                Settings sidebar item. */}
            <Button
              variant="ghost"
              size="sm"
              aria-label="Notification settings"
              onClick={() => {
                onClose();
                navigate("/settings/notifications");
              }}
            >
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <DialogBody className="grid gap-4">
          {/* §5.5 sound preferences: chime only for what genuinely
              interrupts — escalations (default on) and SLA breaches
              (opt-in; a Monday sweep can breach dozens at once). */}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-border py-3">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <Volume2 className="h-3.5 w-3.5" /> Sound
            </span>
            <div className="flex items-center gap-2">
              <Switch
                id="notifications-sound-escalations"
                checked={soundEscalations}
                onCheckedChange={(v) => {
                  setSoundEscalations(v);
                  setEscalationSoundEnabled(v);
                }}
              />
              <Label htmlFor="notifications-sound-escalations" className="text-xs">
                Escalations
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="notifications-sound-sla"
                checked={soundSla}
                onCheckedChange={(v) => {
                  setSoundSla(v);
                  setSlaSoundEnabled(v);
                }}
              />
              <Label htmlFor="notifications-sound-sla" className="text-xs">
                SLA breaches
              </Label>
            </div>
          </div>

          {listQuery.isLoading ? (
            <PageLoading message="Loading notifications…" />
          ) : listQuery.isError ? (
            <PageError
              title="Couldn't load notifications"
              description="Try refreshing."
            />
          ) : items.length === 0 ? (
            <EmptyState
              icon={<Bell className="h-5 w-5" />}
              title={unreadOnly ? "All caught up" : "No notifications"}
              description={
                unreadOnly
                  ? "You have no unread notifications."
                  : "Notifications will show up here as things happen."
              }
            />
          ) : (
            <ul className="divide-y border-y border-border">
              {items.map((n) => (
                <li
                  key={n.id}
                  className={cn(
                    "flex items-start justify-between gap-3 px-3 py-4 text-sm transition-colors hover:bg-muted/40",
                    n.readAt ? "bg-background" : "bg-primary/[0.04]",
                  )}
                >
                  <button
                    type="button"
                    onClick={() => openNotification(n)}
                    className="min-w-0 flex-1 text-left"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      {!n.readAt && (
                        <>
                          <span
                            className="h-2 w-2 shrink-0 rounded-full bg-primary"
                            aria-hidden
                          />
                          <span className="sr-only">Unread.</span>
                        </>
                      )}
                      <span className="font-medium">{notificationTitle(n)}</span>
                      {n.payload.live === true && (
                        <Badge
                          variant="outline"
                          className="border-destructive text-xs font-medium text-destructive"
                        >
                          Live call
                        </Badge>
                      )}
                      <NotificationPresentationBadge
                        event={eventCatalog.get(n.eventKey)}
                      />
                      <span className="text-xs text-muted-foreground">
                        {notificationLabel(n.eventKey)}
                      </span>
                    </div>
                    <NotificationBody body={n.body} users={users} />
                    <span className="mt-1 block text-xs text-muted-foreground">
                      {formatDateTime(n.createdAt)}
                    </span>
                  </button>
                  {!n.readAt && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="shrink-0"
                      aria-label={`Mark “${notificationTitle(n)}” read`}
                      disabled={markRead.isPending}
                      onClick={() => markRead.mutate(n.id)}
                    >
                      Mark read
                    </Button>
                  )}
                </li>
              ))}
              {listQuery.hasNextPage && (
                <li>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    disabled={listQuery.isFetchingNextPage}
                    onClick={() => listQuery.fetchNextPage()}
                  >
                    {listQuery.isFetchingNextPage ? "Loading…" : "Show more"}
                  </Button>
                </li>
              )}
            </ul>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
